import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useGetBranches } from "src/api/generic";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { LoadingButton } from "@mui/lab";
import { specificBranchHasPermission } from "src/utils";
import { InventoryPermissions } from "src/constants/permissions";
import { useGetItemsList } from "src/api/purchasing/items";
import {
  useAddProductionTransMutation,
  useGetProRecipeById,
} from "src/api/inventory/production";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";

interface IModel {
  open: boolean;
  handleClose: () => void;
  row?: any;
  refetch?: Function;
}

const schema = yup.object().shape({
  productionId: yup.number().required("required"),
  branch_id: yup.number().typeError("required").required("required"),
  qnty: yup.number().required("required"),
  date: yup.date().required("required"),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

const CreateProPopup = (props: IModel) => {
  const { open, handleClose, refetch = () => {} } = props;
  const navigate = useNavigate();
  const { t } = useTranslation("inventory");
  const { t: generalT } = useTranslation("general");
  const [notifications, setNotifications] = useRecoilState(notificationsState);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  // APIS
  const { data: branchesData } = useGetBranches();
  const { mutate, status, error, isPending } = useAddProductionTransMutation();
  const { data } = useGetItemsList({ type: "product" });

  const selectedPro = data?.data?.find(
    (item: any) => item?.id === +watch("productionId")
  );

  const {
    data: recipeData,
    refetch: refetchRecipe,
    isFetching: isFetchingRecipe,
  } = useGetProRecipeById({
    enabled: !!selectedPro?.product_recipe_id && !selectedPro?.is_fixed,
    id: selectedPro?.product_recipe_id,
  });

  let recipeCost = 0;

  recipeData?.data?.product_recipe_items?.forEach((item: any) => {
    recipeCost += item?.quantity * item?.item?.unit_cost;
  });

  const totalCost = selectedPro?.is_fixed
    ? watch("qnty") * selectedPro?.unit_cost
    : (watch("qnty") / recipeData?.data?.quantity) * recipeCost;

  // permissions
  const branchesHavePermission =
    branchesData?.data.filter((branch) =>
      specificBranchHasPermission(
        InventoryPermissions.addStocktaking,
        branch.id
      )
    ) || [];

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const { productionId, qnty, branch_id, date } = data;

    mutate({
      branch_id,
      item_id: productionId,
      quantity: qnty,
      total_cost: totalCost ?? 0,
      date: moment(date).format("YYYY-MM-DD"),
    });
  };

  //Add success and error handling
  useEffect(() => {
    if (status === "success") {
      handleClose();
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 537,
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"}>
            {t("createPRO")}
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <FormControl fullWidth size="small" error={!!errors.productionId}>
              <InputLabel id="demo-simple-select-label">
                {t("selectPro")}
              </InputLabel>
              <Controller
                name="productionId"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    key={field.value}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label={t("selectPro")}
                  >
                    {data?.data?.map((item: any) => {
                      return (
                        <MenuItem
                          key={item.id}
                          value={`${item.id}`}
                          // disabled={!active_subscriptions?.package.has_items}
                        >
                          {item.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              />
              <FormHelperText id="my-helper-text">
                {generalT(`${errors.productionId?.message ?? ""}`)}
              </FormHelperText>
            </FormControl>
            <FormControl fullWidth size="small" error={!!errors.branch_id}>
              <InputLabel id="demo-simple-select-label">
                {generalT("branch")}
              </InputLabel>
              <Controller
                name="branch_id"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    key={field.value}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label={generalT("branch")}
                  >
                    {branchesHavePermission?.map(
                      ({ id, name, active_subscriptions }) => {
                        return (
                          <MenuItem key={id} value={`${id}`}>
                            {name}
                          </MenuItem>
                        );
                      }
                    )}
                  </Select>
                )}
              />
              <FormHelperText id="my-helper-text">
                {generalT(`${errors.branch_id?.message ?? ""}`)}
              </FormHelperText>
            </FormControl>
            <Stack
              direction={"row"}
              gap={{ xs: 2, md: 5 }}
              alignItems={"center"}
            >
              {selectedPro && (
                <>
                  <Typography
                    fontSize={"14px"}
                    fontWeight={600}
                    color={"#667085"}
                  >
                    {selectedPro?.name ?? ""}
                  </Typography>
                  <Box>
                    {/* qnty */}
                    <Controller
                      name="qnty"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          type="number"
                          fullWidth
                          label={t("qnty")}
                          size="small"
                          {...field}
                          error={!!errors.qnty}
                          helperText={errors.qnty?.message}
                          sx={{ width: "140px" }}
                        />
                      )}
                    />
                  </Box>

                  <Box>
                    <Typography
                      color={"#667085"}
                      fontSize={"14px"}
                      lineHeight={"24px"}
                    >
                      {t("unit")}
                    </Typography>
                    <Typography
                      color={"#667085"}
                      fontSize={"14px"}
                      lineHeight={"24px"}
                      fontWeight={700}
                    >
                      {selectedPro.unit?.name}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      color={"#667085"}
                      fontSize={"14px"}
                      lineHeight={"24px"}
                    >
                      {t("cost")}
                    </Typography>
                    <Typography
                      color={"#667085"}
                      fontSize={"14px"}
                      lineHeight={"24px"}
                      fontWeight={700}
                    >
                      {totalCost}
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.date,
                      helperText: t(`${errors.date?.message ?? ""}`),
                      size: "small",
                    },
                  }}
                  format={"dd/MM/yyyy"}
                  label={t("date")}
                  maxDate={new Date()}
                />
              )}
            />
          </Stack>
          <Stack mt={3} spacing={2} direction={"row"}>
            <Button
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={handleClose}
            >
              {generalT("cancel")}
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              loading={isPending}
              disabled={isFetchingRecipe}
            >
              {generalT("submit")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default CreateProPopup;
